import { useEffect, useRef, useState } from "react";
import { Play, Pause, Info, Music, Download, Music2, Music2Icon } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { downloadMasterTrack, retrieveFinalMix, uploadFileFromUrls, buildRetrieveFinalMixBody, validateRetrieveFinalMixBody } from "../../../../redux/actions/audioEngineer";
import { showToast } from "../../../../helper/ShowToast";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tooltip } from "../../../../components/Tooltip";
import { MixingEngineerPackModal } from "./MixingEngineerPackModal";
import ProcessingLoader from "./components/ProcessingLoader";
import DropdownMenu from "./components/DropdownMenu";
import { useNavigate } from "react-router-dom";

export default function PreviewSection({ demoMode = false, demoMasterMode = false }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const audioRef = useRef(null);
  const svgRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false)
  const [isMastering, setIsMastering] = useState(false)
  const [showAudioEngineerModal, setShowAudioEngineerModal] = useState(false);
  const [finalMixData, setFinalMixData] = useState(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // from audio when NOT dragging
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [isAudioLoading, setIsAudioLoading] = useState(false);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(null); // temp progress while dragging
  const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState(false);

  const audioEngineerReducer = useSelector((state) => state.audioEngineer);
  const mixPreviewDoc = audioEngineerReducer?.mixPreview?.data;
  const mixPreviewFields =
    mixPreviewDoc && (mixPreviewDoc._id || mixPreviewDoc.roex_task_id)
      ? mixPreviewDoc
      : {};
  const { projectName, mixedPreviewUrl, mixedUrl, isFinalMixed } = mixPreviewFields;
  const mixMetaData = audioEngineerReducer?.mixMetaData;
  const uploaded = audioEngineerReducer?.uploadedData?.data || {};

  let previewArray = [];
  if (Array.isArray(audioEngineerReducer?.preview)) {
    previewArray = audioEngineerReducer.preview;
  } else if (Array.isArray(audioEngineerReducer?.preview?.data)) {
    previewArray = audioEngineerReducer.preview.data;
  } else if (audioEngineerReducer?.preview && typeof audioEngineerReducer.preview === 'object') {
    // treat single preview object as an array with one item
    previewArray = [audioEngineerReducer.preview];
  } else {
    previewArray = [];
  }

  const previewUrl = mixedPreviewUrl || null;

  const [masterMix, setMasterMix] = useState(false);
  const localLoudness = localStorage.getItem('loundness');
  const localSampleRate = localStorage.getItem('sampleRate');
  const [loudness, setLoudness] = useState(localLoudness || "MEDIUM");
  const [sampleRate, setSampleRate] = useState(localSampleRate || "48000");
  const isMixedReady = isFinalMixed;

  // SVG geometry
  const VIEWBOX = 220;
  const CX = VIEWBOX / 2; // 110
  const CY = VIEWBOX / 2; // 110
  const RADIUS = 90;

  const lastSeekRef = useRef(0);
  const IGNORE_AFTER_SEEK_MS = 250;
  const handledPlayRef = useRef(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMeta = () => {
      setDuration(audio.duration || 0);
      setIsAudioLoading(false);
    };

    const onCanPlay = () => {
      setIsAudioLoading(false);
    };

    const onLoadStart = () => {
      setIsAudioLoading(true);
    };

    const onTimeUpdate = () => {
      const now = Date.now();
      if (now - lastSeekRef.current < IGNORE_AFTER_SEEK_MS) return;

      const t = audio.currentTime || 0;
      setCurrentTime(t);
      if (!isDragging && audio.duration) {
        setProgress((t / audio.duration) * 100);
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };

    const onError = (e) => {
      console.error("Audio error", e);
      setError("Failed to load audio");
      setIsPlaying(false);
    };

    audio.addEventListener("loadedmetadata", onLoadedMeta);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("loadstart", onLoadStart);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMeta);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("loadstart", onLoadStart);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [isDragging, previewUrl, mixedUrl, isFinalMixed]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    const src = isFinalMixed ? mixedUrl : previewUrl;
    if (src) {
      try {
        audio.src = src;
        audio.load();
        const playNow = !!audioEngineerReducer?.preview?.playNow;
        if (playNow && !handledPlayRef.current) {
          audio.play().then(() => {
            setIsPlaying(true);
          }).catch((err) => {
            console.debug('Autoplay attempt failed', err);
            setIsPlaying(false);
          });
          handledPlayRef.current = true;
        }
      } catch (e) {
        console.warn('Failed to set audio.src', e);
      }
    }
  }, [previewUrl, mixedUrl, isFinalMixed]);

  useEffect(() => {
    handledPlayRef.current = false;
  }, [audioEngineerReducer?.preview]);

  useEffect(() => {
    if (audioEngineerReducer?.previewStatus) {
      setLoudness(audioEngineerReducer.previewStatus.loudness || "MEDIUM");
      setSampleRate(audioEngineerReducer.previewStatus.sampleRate || "48000");
    }
  }, [audioEngineerReducer?.previewStatus]);

  const downloadAudioFile = (url, format, fileName) => {
    const reqBody = {
      url: url,
      format: format,
      fileName: fileName,
    }
    dispatch(downloadMasterTrack(reqBody, setIsDownloading))
  }

  const getDownloadItems = () => [
    {
      label: "Download as WAV file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = mixedUrl || null;
        const name = projectName || "final_mixed";
        return downloadAudioFile(url, "wav", name);
      },
    },
    {
      label: "Download as MP3 file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = mixedUrl || null;
        const name = projectName || "final_mixed";
        return downloadAudioFile(url, 'mp3', name);
      },
    },
    {
      label: "Download as M4A file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = mixedUrl || null;
        const name = projectName || "final_mixed";
        return downloadAudioFile(url, 'm4a', name);
      },
    },
    {
      label: "Download as AAC file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = mixedUrl || null;
        const name = projectName || "final_mixed";
        return downloadAudioFile(url, 'aac', name);
      },
    },
    {
      label: "Download as OGG file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = mixedUrl || null;
        const name = projectName || "final_mixed";
        return downloadAudioFile(url, 'ogg', name);
      },
    },
    {
      label: "Download as AIFF file",
      icon: <Music size={16} />,
      onClick: () => {
        const url = mixedUrl || null;
        const name = projectName || "final_mixed";
        return downloadAudioFile(url, 'aiff', name);
      },
    }
  ];

  // Play / Pause
  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        await audio.play();
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
    } catch (err) {
      console.error("Playback error", err);
      // setError("Playback failed");
    }
  };

  // Convert a client coordinate (mouse/touch) into 0..100 progress
  const clientToProgress = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;

    // angle in degrees, 0 at +X, increase counter-clockwise. We want 0% at 12 o'clock.
    let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI; // -180..180, 0 at +X
    angleDeg += 90; // rotate so 0 corresponds to 12 o'clock
    if (angleDeg < 0) angleDeg += 360;
    return (angleDeg / 360) * 100;
  };

  // Start dragging: pause audio (if playing), set state and attach global listeners
  const startDrag = (clientX, clientY) => {
    const audio = audioRef.current;
    const initial = clientToProgress(clientX, clientY) ?? 0;

    setWasPlayingBeforeDrag(Boolean(audio && !audio.paused && !audio.ended));
    try {
      if (audio && !audio.paused) audio.pause();
    } catch (e) {
      // ignore
    }

    setDragProgress(initial);
    setIsDragging(true);

    // attach listeners
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("touchmove", onPointerMove, { passive: false });
    window.addEventListener("touchend", onPointerUp);

    // prevent text selection while dragging
    document.body.style.userSelect = "none";
  };

  // Pointer handlers — handle both mouse & touch
  const onPointerDown = (e) => {
    e.preventDefault();
    const isTouch = e.type === "touchstart";
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    startDrag(clientX, clientY);
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    // prevent page scroll on touch
    if (e.cancelable) e.preventDefault();
    const isTouch = e.type === "touchmove";
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;

    const p = clientToProgress(clientX, clientY);
    if (p != null) setDragProgress(p);
  };

  const onPointerUp = (e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();

    // compute final progress — try to use last move position, else derive from up event
    let final = dragProgress;
    if (final == null) {
      const isTouch = e.type === "touchend";
      if (!isTouch && e.clientX != null) {
        final = clientToProgress(e.clientX, e.clientY) ?? final;
      }
    }
    if (final == null) final = 0;

    const audio = audioRef.current;
    if (audio && audio.duration && !isNaN(audio.duration)) {
      const newTime = (final / 100) * audio.duration;
      // record seek time so we can ignore noisy timeupdate events for a short while
      lastSeekRef.current = Date.now();
      try {
        audio.currentTime = newTime;
      } catch (err) {
        console.warn("Seeking failed:", err);
      }

      setProgress(final);
      setCurrentTime(newTime);
    }

    if (audio && wasPlayingBeforeDrag) {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      setIsPlaying(Boolean(audio && !audio.paused));
    }

    setIsDragging(false);
    setDragProgress(null);
    setWasPlayingBeforeDrag(false);

    // cleanup listeners
    window.removeEventListener("mousemove", onPointerMove);
    window.removeEventListener("mouseup", onPointerUp);
    window.removeEventListener("touchmove", onPointerMove);
    window.removeEventListener("touchend", onPointerUp);

    document.body.style.userSelect = "";
  };

  const activeProgress = dragProgress != null ? dragProgress : progress || 0;

  const pointerAngle = (activeProgress / 100) * 360 - 90;
  const pointerRad = (pointerAngle * Math.PI) / 180;
  const pointerX = CX + RADIUS * Math.cos(pointerRad);
  const pointerY = CY + RADIUS * Math.sin(pointerRad);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", onPointerUp);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
      document.body.style.userSelect = "";
    };
  }, []);

  const handleFinalMixCreation = () => {
    const requestBody = buildRetrieveFinalMixBody(audioEngineerReducer, {
      createMaster: masterMix,
      desiredLoudness: loudness,
      sampleRate: sampleRate,
    });
    const validationError = validateRetrieveFinalMixBody(
      requestBody,
      mixMetaData,
      audioEngineerReducer?.mixPreview
    );

    if (validationError) {
      showToast('warning', validationError);
      return;
    }

    if (isFinalMixed) {
      showToast('warning', 'This mix is already finalized. Start a new upload for another final mix.');
      return;
    }

    setFinalMixData(requestBody);
    setShowAudioEngineerModal(true);
  }

  const handleMasterCreation = () => {
    try {
      const requestBody = {
        sourceUrls: [mixedUrl]
      }
      dispatch(uploadFileFromUrls(requestBody, setIsMastering, 'mastering', navigate))
    } catch (e) {
      console.debug('Move to Mastering failed', e);
    }
  }

  return (
    <div className="bg-white flex flex-col items-center gap-4 relative mx-auto rounded-2xl p-4 lg:p-5">
      <ToastContainer />
      {isProcessing ? (
        <ProcessingLoader isMixed uploadId={mixPreviewDoc._id || null} taskId={mixPreviewDoc.roex_task_id || null} isDemo={demoMode || demoMasterMode}/>
      ) :
        (
          <>
            <h2 className="text-[#FA6138] text-base font-semibold text-center">{demoMode || demoMasterMode ? "TrackName.mp3" : projectName}</h2>

            {/* Circular Player */}
            <div className="relative w-[200px] h-[200px]">
              <svg ref={svgRef} className="w-full h-full" viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} aria-hidden>
                {/* static orange ring */}
                <circle cx={CX} cy={CY} r={RADIUS} stroke="#FA6138" strokeWidth="6" fill="none" />

                {/* subtle background track */}
                <circle cx={CX} cy={CY} r={RADIUS} stroke="rgba(26,26,58,0.08)" strokeWidth="2" fill="none" />

                {/* draggable pointer */}
                <circle
                  cx={pointerX}
                  cy={pointerY}
                  r={9}
                  fill="#FA6138"
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ cursor: isDragging ? "grabbing" : "grab" }}
                  onMouseDown={onPointerDown}
                  onTouchStart={onPointerDown}
                  filter="url(#glow)"
                />

                {/* glow definition */}
                <defs>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* center visual */}
                <g transform={`translate(${CX - 36}, ${CY - 36})`}>
                  <rect rx={36} ry={36} width={72} height={72} fill="#00067e" />
                </g>
              </svg>

              {/* Center Play/Pause button */}
              <button
                onClick={togglePlay}
                aria-label={isPlaying ? "Pause" : "Play"}
                className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[130px] h-[130px] rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: "#00067e" }}
              >
                {isAudioLoading ? (
                  <div className="relative w-full h-full flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full border-4 border-theme_clr2 border-t-transparent animate-spin"></div>
                  </div>
                ) : isPlaying ? (
                  <Pause className="w-8 h-8" />
                ) : (
                  <Play className="w-8 h-8" />
                )}
              </button>
            </div>

            {/* Audio element */}
            <audio
              ref={audioRef}
              src={isFinalMixed ? mixedUrl : previewUrl}
              preload="metadata"
              onError={(e) => {
                console.error("Audio load error", e);
                setError("Failed to load audio");
              }}
            />

            {/* Mastering Section */}
            {isMixedReady && (
              <div className="px-6 pb-4">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-theme_clr2 font-semibold text-lg">Mastering</span>
                  <Tooltip
                    text="Mastering"
                    style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                    position="right"
                  >
                    <Info className="w-4 h-4 text-theme_clr2" />
                  </Tooltip>
                </div>
                {/* Mastering Options */}
                <div className="space-y-4">
                  <div className={`grid grid-cols-1 lg:grid-cols-1 gap-4`}>
                    {/* Master Mix */}
                    <div>
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-theme_clr2 text-sm">Would you like to master your mix?</span>
                        <Tooltip
                          text="Mastering applies final polish—loudness, EQ, and dynamics—for release-ready quality."
                          style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                          position="right"
                        >
                          <Info className="w-4 h-4 text-theme_clr2" />
                        </Tooltip>
                      </div>
                      <div className="flex gap-3 justify-center">
                        <button
                          onClick={() => setMasterMix(true)}
                          className={`w-28 py-1 rounded-full text-sm font-normal transition ${masterMix
                            ? 'bg-theme_clr1 text-theme_clr2'
                            : 'bg-white text-theme_clr2 border border-theme_clr1'
                            }`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setMasterMix(false)}
                          className={`w-28 py-1 rounded-full text-sm font-normal transition ${!masterMix
                            ? 'bg-theme_clr1 text-theme_clr2'
                            : 'bg-white text-theme_clr2 border border-theme_clr1'
                            }`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isMixedReady || demoMasterMode ? (
              <div className="flex flex-row items-center justify-center gap-4 w-full">
                <DropdownMenu
                  button={
                    <button disabled={isDownloading} className="w-full max-w-md bg-theme_clr2 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium flex items-center justify-center gap-2">
                      {isDownloading ? (
                        <div className="relative w-6 h-6 flex items-center justify-center">
                          <div className="w-6 h-6 rounded-full border-2 border-[#FFFFFF] border-t-transparent animate-spin"></div>
                        </div>
                      ) : (
                        <>
                          <span className="flex items-center justify-center h-6 w-6">
                            <Download size={16} />
                          </span>
                          <span className="truncate">Download Mixed Track</span>
                        </>
                      )}
                    </button>
                  }
                  fullWidth={true}
                  items={getDownloadItems()}
                />

                {masterMix && (
                  <Tooltip
                    text={"Your mixed track can be found in your Mix Library."}
                    style={{ bg: "bg-white", text: "text-theme_clr4", width: "w-48" }}
                    position="top-right"
                  >
                    <div className="relative group w-full max-w-md">
                      <button
                        onClick={handleMasterCreation}
                        disabled={isMastering}
                        className={`w-full bg-[#FA6138] text-white py-3 px-4 rounded-xl hover:bg-[#ff724d] transition-colors font-medium flex items-center justify-center gap-2 ${isMastering ? 'opacity-70 cursor-not-allowed' : 'opacity-100'}`}
                      >
                        <span className="flex items-center justify-center h-6 w-6 shrink-0">
                          <Music2Icon size={16} />
                        </span>
                        <span className="truncate">
                          {isMastering ? "Processing for Mastering..." : "Proceed to Create Master"}
                        </span>
                      </button>
                    </div>
                  </Tooltip>
                )}
              </div>
            ) : (
              <button
                onClick={handleFinalMixCreation}
                disabled={isProcessing}
                className={`mt-4 w-full max-w-md bg-[#FA6138] text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium ${isProcessing ? 'opacity-70 cursor-not-allowed' : 'opacity-100'}`}
              >
                {isProcessing ? 'Processing your Final Mix track...' : 'Get your release-ready mixed track'}
              </button>
            )}

            {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
            <MixingEngineerPackModal
              isOpen={showAudioEngineerModal}
              onClose={() => setShowAudioEngineerModal(false)}
              mixRequestData={finalMixData}
              getMixRequestData={() => buildRetrieveFinalMixBody(audioEngineerReducer, {
                createMaster: masterMix,
                desiredLoudness: loudness,
                sampleRate: sampleRate,
              })}
              validateMixRequest={(body) => validateRetrieveFinalMixBody(body, mixMetaData, audioEngineerReducer?.mixPreview)}
              setIsProcessing={setIsProcessing}
            />
          </>
        )}
    </div>
  );
}

